import { combineReducers, compose } from '@reduxjs/toolkit';

import slice from './stage_dimensions.slice';

import rooms, { slice as wallPreviewRooms } from 'domains/wall_preview_rooms';

import type { WallPreviewDimension } from './stage_dimensions.slice';

/**
 * Represents the slice as it exists in the final store's reducer
 *
 * Since we have dependencies on other state trees, we include those
 * in the GlobalState type
 */
const combined = combineReducers({
  [slice.name]: slice.reducer,
  [wallPreviewRooms.name]: wallPreviewRooms.reducer,
});
type GlobalState = ReturnType<typeof combined>;

/** Get the local state for the current domain */
const localState = (state: GlobalState) => state[slice.name];
type LocalState = ReturnType<typeof localState>;

const selectScreenDimensions = (state: LocalState) => state.screen;
const selectWallPreviewDimensions = (state: LocalState) => state.wallPreview;
const selectWallPreviewBounds = (state: LocalState) => {
  const { screen } = state;
  const wallPreview = state.wallPreview as WallPreviewDimension;

  return {
    leftLimit: screen.width / 2 - wallPreview.width / 2,
    rightLimit: screen.width / 2 + wallPreview.width / 2,
    topLimit: screen.height / 2 - wallPreview.height / 2,
    bottomLimit: screen.height / 2 + wallPreview.height / 2,
  };
};
const selectStageZoom = (state: LocalState) => state.zoom;

const selectWallPreviewActive = (state: GlobalState) => {
  const localized = localState(state);
  const hasRoomSrc = !!rooms.selectors.whichWallPreview(state)?.fullSrc;
  const hasWallPreviewSize = !!localized.wallPreview && !!localized.wallPreview.naturalWidth;
  return hasRoomSrc && hasWallPreviewSize;
};

export default {
  screenDimensions: compose(selectScreenDimensions, localState),
  wallPreviewDimensions: compose(selectWallPreviewDimensions, localState),
  wallPreviewBounds: compose(selectWallPreviewBounds, localState),
  zoom: compose(selectStageZoom, localState),
  // Already globalized
  wallPreviewActive: selectWallPreviewActive,
};
